// api.js
import axios from "axios";
import API_BASE_URL from "./config";

// Deine API-URL - anpassen je nach Setup
//const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:44575/api";

// Erstelle eine Axios-Instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true // wichtig, wenn du HttpOnly-Refresh-Cookies benutzt
});

// Falls du Access Tokens in localStorage speicherst:
function getAccessToken() {
  return localStorage.getItem("access_token");
}

function setAccessToken(token) {
  localStorage.setItem("access_token", token);
}

function removeAccessToken() {
  localStorage.removeItem("access_token");
}

// Request-Interceptor: Setze Bearer-Token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag, ob wir gerade ein Refresh ausführen
let isRefreshing = false;
// Warteschlange für Requests, die anstehen, während Refresh läuft
let refreshSubscribers = [];

// Wartende Requests informieren, sobald neuer Token da ist
function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

// Ein Request kann sich hier „anmelden“, um nach dem Refresh wiederholt zu werden
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

// Response-Interceptor: Bei 401 => Refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Wenn 401
    if (error.response && error.response.status === 401) {
      // Sonderfall: Kein Refresh bei .../refresh => Endlosschleife
      if (originalRequest.url.includes("/refresh")) {
        // Dann ist refresh gescheitert => Logout
        logout();
        return Promise.reject(error);
      }

      // Wenn originalRequest bereits einmal _retry hatte, Refresh also schon versucht
      if (originalRequest._retry) {
        // Dann Refresh offensichtlich gescheitert => Logout
        logout();
        return Promise.reject(error);
      }

      // Speziellen Fall für 403 (Forbidden) hinzufügen
      if (error.response && error.response.status === 403) {
        // Bei Berechtigungsproblemen (z.B. abgelaufene Berechtigung)
        console.warn("Permission denied (403), redirecting to login");
        // Entferne Token und leite zum Login um
        removeAccessToken();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // Markiere den Request, damit er nicht nochmal unendlich retried
      originalRequest._retry = true;

      // Sind wir bereits am Refreshen?
      if (isRefreshing) {
        // Dann warten wir: wir legen eine Promise an,
        // die erst aufgelöst wird, wenn der Refresh fertig ist.
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            // OriginalRequest mit dem neuen Token ausführen
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      // Wir starten jetzt den Refresh
      isRefreshing = true;
      try {
        const newToken = await refreshTokens(); // ruft /api/refresh auf
        isRefreshing = false;
        // Alle wartenden Requests informieren
        onRefreshed(newToken);
        // originalRequest nochmal senden, mit neuem Token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        // Refresh fehlgeschlagen => logout
        logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Refresh-Funktion
async function refreshTokens() {
  // Ruft /api/refresh. Hier GET oder POST - je nach Backend
  const res = await api.get("/refresh");
  // JSON: { access_token, refresh_token? }
  const newToken = res.data.access_token;
  // Access Token in localStorage speichern
  setAccessToken(newToken);
  return newToken;
}

function logout() {
  removeAccessToken();
  // Wenn gewünscht, /api/logout aufrufen:
  // api.get("/logout").catch(() => {});
  // Dann weiterleiten:
  window.location.href = "/login";
}

export { api, logout, getAccessToken, setAccessToken, removeAccessToken };
